import { useEffect, useRef, useState } from "react"
import { Box, InputProps, Text, TextProps } from "@chakra-ui/react"

type TokenAmountInputTokenLabelProps = TextProps & {
  tokenAmount: string
  tokenLabel: string
  /**
   * Props used in the input. This is needed to properly calculate the position
   * of the label.
   */
  inputProps: InputProps
  defaultLabelPosition?: number
  textOffset?: number
}

export default function TokenAmountInputTokenLabel({
  tokenAmount,
  tokenLabel,
  inputProps,
  defaultLabelPosition = 60,
  textOffset = 38,
  ...props
}: TokenAmountInputTokenLabelProps) {
  const textRef = useRef<HTMLParagraphElement>(null)
  const [inputTextWidth, setInputTextWidth] = useState(0)

  const {
    height,
    width,
    paddingLeft,
    paddingRight,
    fontSize,
    lineHeight,
    fontWeight,
    color,
  } = inputProps

  useEffect(() => {
    setInputTextWidth(textRef?.current?.offsetWidth ?? 0)
  }, [tokenAmount])

  return (
    <>
      {/**
       * Non-visible box needed to calculate the exact width of the text in the
       * input. The text has the exact same styles and width as the text in the
       * actual input thanks to the props we've passed in inputProps.
       */}
      <Box
        visibility="hidden"
        whiteSpace="nowrap"
        maxHeight={height}
        height="auto"
        width={width}
        pl={paddingLeft}
        pr={paddingRight}
        position="absolute"
        fontSize={fontSize}
        lineHeight={lineHeight}
        fontWeight={fontWeight}
        color={color}
      >
        <Text
          ref={textRef}
          overflow="hidden"
          width="fit-content"
          maxWidth="100%"
        >
          {tokenAmount}
        </Text>
      </Box>
      <Text
        fontSize={fontSize}
        lineHeight={lineHeight}
        fontWeight={fontWeight}
        position="absolute"
        color={color || "primary.black.100"}
        top="36px"
        left={`${
          tokenAmount.length > 1
            ? textOffset + inputTextWidth
            : defaultLabelPosition
        }px`}
        zIndex={10}
        {...props}
      >
        {tokenLabel}
      </Text>
    </>
  )
}
